import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLanguage,
  FaUserTag,
  FaClock,
  FaMoneyBillWave,
  FaInfoCircle,
  FaCheckCircle,
  FaHandsHelping,
  FaHeartbeat,
  FaProcedures,
  FaExclamationTriangle,
  FaShieldAlt,
} from "react-icons/fa";
import config, { getUserCity, fetchRemoteConfig } from "../../../config";
import TermsAcceptStep from "./TermsAcceptStep";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";

// Care Services Required — grouped by care type. NURSING PROCEDURES and
// VITAL MONITORING are the two groups that need an actual trained/clinical
// caretaker (Home Nursing Support), not a general patient attendant — see
// requiresClinicalSkill below, which drives the validation banner.
export const PATIENT_CARE_GROUPS = [
  {
    group: "Nursing Care",
    clinical: false,
    options: [
      { value: "patient-hygiene", label: "Patient Hygiene" },
      { value: "bathing-assistance", label: "Bathing Assistance" },
      { value: "feeding-assistance", label: "Feeding Assistance" },
      { value: "position-repositioning", label: "Position/Repositioning" },
      { value: "bedridden-patient-care", label: "Bedridden Patient Care" },
      { value: "comfort-care", label: "Comfort Care" },
    ],
  },
  {
    group: "Vital Monitoring",
    clinical: true,
    options: [
      { value: "bp", label: "BP" },
      { value: "temperature", label: "Temperature" },
      { value: "pulse", label: "Pulse" },
      { value: "spo2", label: "SpO2 (Oxygen Saturation)" },
    ],
  },
  {
    group: "Nursing Procedures",
    clinical: true,
    options: [
      { value: "wound-dressing", label: "Wound Dressing" },
      { value: "catheter-care", label: "Catheter Care" },
      { value: "feeding-tube-care", label: "Feeding Tube Care" },
      { value: "prescribed-medication-administration", label: "Prescribed Medication Administration" },
      { value: "injection", label: "Injection" },
      { value: "other-prescribed-procedure", label: "Other Prescribed Procedure" },
    ],
  },
  {
    group: "Recovery Care",
    clinical: false,
    options: [
      { value: "post-operative-care", label: "Post-Operative Care" },
      { value: "post-hospitalization-care", label: "Post-Hospitalization Care" },
      { value: "recovery-monitoring", label: "Recovery Monitoring" },
      { value: "nursing-observation", label: "Nursing Observation" },
    ],
  },
];

// value -> which group it came from, so the clinical-skill check is O(1)
// per selected item instead of re-scanning every group on each render.
const CLINICAL_VALUES = new Set(
  PATIENT_CARE_GROUPS.filter((g) => g.clinical).flatMap((g) => g.options.map((o) => o.value))
);

const RELATIONSHIP_OPTIONS = ["Self", "Parent", "Grandparent", "Spouse", "Child", "Relative", "Other"];

const PatientCare = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError,  setSubmitError]  = useState("");
  const [step, setStep] = useState("form"); // "form" -> "terms"

  const categoryKey  = "care-services";
  const serviceKey   = "patient-caretaker";
  // City/Area come from the admin-configured backend list (config_data
  // collection), not a hardcoded array — see the same pattern's comment
  // in ElderCare.js for why this re-syncs on mount instead of trusting
  // the module-level default.
  const [areaOptions, setAreaOptions] = useState(config.AREA_OPTIONS || {});
  useEffect(() => {
    fetchRemoteConfig().then(() => setAreaOptions({ ...config.AREA_OPTIONS }));
  }, []);

  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService  = config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];
  const serviceLabel     = selectedService?.label || "Patient Care";
  const categoryLabel    = selectedCategory?.label || "Care Services";

  const [formData, setFormData] = useState({
    // Requester's own contact details
    username: "", mobilenumber: "", email: "", address: "",
    city: "", area: "", language: "",

    // About the patient
    patientName: "", patientAge: "", patientGender: "", relationship: "",
    currentCondition: "", postSurgery: false, bedridden: false,
    hospitalDischarge: false, doctorInstructions: "",

    // Care services required — every listed service is included automatically
    // (see the "no selection" change below), so this starts pre-populated.
    service_type: PATIENT_CARE_GROUPS.flatMap((g) => g.options).map((o) => o.value),

    // Timing & caretaker preference
    gender: "", urgencyStatus: "", duration: "", shiftTime: "", shiftDuration: "", experienceLevel: "", salaryOffered: "",
  });

  const [showExperienceInfo, setShowExperienceInfo] = useState(false);

  const availableAreas = useMemo(() => {
    const sel = areaOptions[formData.city] || areaOptions[formData.city?.toLowerCase()];
    if (Array.isArray(sel)) return sel;
    if (sel?.areas) return sel.areas;
    return [];
  }, [formData.city, areaOptions]);

  // ── Important validation ────────────────────────────────────────────────
  // Selected Requirement -> does it require clinical/nursing skill?
  //   YES -> Vital Monitoring / Nursing Procedures items were picked; this
  //          needs a trained nurse (Home Nursing Support), not a general
  //          patient attendant.
  //   NO  -> only Nursing Care / Recovery Care items were picked; a general
  //          patient caretaker can handle it.
  const requiresClinicalSkill = useMemo(
    () => formData.service_type.some((v) => CLINICAL_VALUES.has(v)),
    [formData.service_type]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = name === "mobilenumber" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setFormData((prev) => ({ ...prev, [name]: cleanValue, ...(name === "city" ? { area: "" } : {}) }));
  };

  // Yes/No fields (Post-Surgery? / Bedridden? / Hospital Discharge?)
  const handleYesNoToggle = (name, val) => {
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  // Step 1 -> Step 2: validate the form, then show Terms & Conditions.
  // The actual POST only happens from submitRequirement(), reached via the
  // Terms step's checkbox + Submit button — see TermsAcceptStep.
  const handleNext = (e) => {
    e.preventDefault();
    setSubmitError("");
    setStep("terms");
  };

  const submitRequirement = async () => {
    setSubmitError("");

    const detailedServiceList = PATIENT_CARE_GROUPS.flatMap((g) =>
      g.options
        .filter((item) => formData.service_type.includes(item.value))
        .map((item) => ({
          type: "patient-care", group: g.group, key: item.value, value: item.value, label: item.label,
        }))
    );

    const requestBody = {
      // Requester's own contact details
      username: formData.username,
      mobilenumber: formData.mobilenumber,
      email: formData.email,
      address: formData.address,
      city: formData.city,
      area: formData.area,
      language: formData.language,

      // About the patient
      patientName: formData.patientName,
      patientAge: formData.patientAge,
      patientGender: formData.patientGender,
      relationship: formData.relationship,
      currentCondition: formData.currentCondition,
      postSurgery: formData.postSurgery,
      bedridden: formData.bedridden,
      hospitalDischarge: formData.hospitalDischarge,
      doctorInstructions: formData.doctorInstructions,

      // Care services required
      service_list: formData.service_type,
      detailed_service_list: detailedServiceList,
      service_type: "patient-care-services",
      requiresClinicalSkill,

      // Timing & caretaker preference
      gender: formData.gender,
      urgencyStatus: formData.urgencyStatus,
      duration: formData.duration,
      shiftTime: formData.shiftTime,
      shiftDuration: formData.shiftDuration,
      experienceLevel: formData.experienceLevel,
      salaryOffered: formData.salaryOffered,

      service_category: categoryKey,
      selected_city: getUserCity(),
      service: serviceKey,
      category_label: categoryLabel,
      service_label: serviceLabel,
      terms_accepted: true,
    };

    try {
      setIsSubmitting(true);
      const res = await fetch(`${BASE_URL}/api/requirements/patient-care/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();

      if (res.ok && data?.success !== false) {
        setShowThankYou(true);
        setStep("form");
        setFormData({
          username: "", mobilenumber: "", email: "", address: "", city: "", area: "", language: "",
          patientName: "", patientAge: "", patientGender: "", relationship: "",
          currentCondition: "", postSurgery: false, bedridden: false,
          hospitalDischarge: false, doctorInstructions: "",
          service_type: PATIENT_CARE_GROUPS.flatMap((g) => g.options).map((o) => o.value),
          gender: "", urgencyStatus: "", duration: "", shiftTime: "", shiftDuration: "", experienceLevel: "", salaryOffered: "",
        });
      } else {
        setSubmitError(data?.message || data?.error || "Failed to submit requirement.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      setSubmitError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    page:        { minHeight:"100vh", background:"linear-gradient(135deg,#ecfdf5 0%,#d1fae5 50%,#eef2ff 100%)", padding:"30px 20px", fontFamily:"Segoe UI,Tahoma,Geneva,Verdana,sans-serif" },
    wrapper:     { maxWidth:"1580px", width:"96%", margin:"0 auto" },
    hero:        { background:"linear-gradient(135deg,#0f4c3a,#059669,#34d399)", color:"#fff", borderRadius:"28px", padding:"34px 36px", boxShadow:"0 20px 40px rgba(5,150,105,0.22)", marginBottom:"24px" },
    heroBadge:   { display:"inline-block", background:"rgba(255,255,255,0.14)", padding:"7px 14px", borderRadius:"999px", fontSize:"12px", fontWeight:"700", marginBottom:"12px" },
    heroTitle:   { margin:0, fontSize:"2.1rem", fontWeight:"800" },
    heroText:    { marginTop:"10px", marginBottom:0, color:"rgba(255,255,255,0.9)", maxWidth:"900px", lineHeight:1.6, fontSize:"15px" },
    formCard:    { background:"#fff", borderRadius:"28px", border:"1px solid #d1fae5", boxShadow:"0 14px 34px rgba(15,23,42,0.08)", overflow:"hidden", width:"100%", maxWidth:"1320px", margin:"0 auto" },
    formHeader:  { padding:"28px 40px", borderBottom:"1px solid #d1fae5", background:"linear-gradient(180deg,#ffffff,#f0fdf4)" },
    formTitle:   { margin:0, fontSize:"1.6rem", fontWeight:"800", color:"#0f172a" },
    formSub:     { margin:"8px 0 0", color:"#64748b", fontSize:"14px" },
    body:        { padding:"32px 40px 36px" },
    section:     { marginBottom:"28px", padding:"24px", borderRadius:"20px", background:"#f0fdf4", border:"1px solid #bbf7d0" },
    sectionTitle:{ margin:"0 0 18px", fontSize:"1.1rem", fontWeight:"800", color:"#0f172a", display:"flex", alignItems:"center", gap:"8px" },
    grid:        { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:"20px", alignItems:"start" },
    field:       { display:"flex", flexDirection:"column", gap:"8px" },
    label:       { fontSize:"14px", fontWeight:"700", color:"#334155", display:"flex", alignItems:"center", gap:"8px" },
    input:       { width:"100%", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box" },
    select:      { width:"100%", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box" },
    infoBtn:     { border:"none", background:"none", padding:0, margin:0, color:"#059669", fontSize:"15px", cursor:"pointer", display:"inline-flex", alignItems:"center" },
    infoPopover: { position:"absolute", top:"calc(100% + 6px)", left:0, right:0, zIndex:5, background:"#fff", border:"1px solid #d6deeb", borderRadius:"12px", boxShadow:"0 10px 28px rgba(15, 23, 42, 0.14)", padding:"14px 16px" },
    textarea:    { width:"100%", minHeight:"120px", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box", resize:"vertical" },
    submitWrap:  { display:"flex", justifyContent:"center", marginTop:"30px" },
    submitBtn:   { border:"none", borderRadius:"999px", background:"linear-gradient(135deg,#059669,#10b981)", color:"#fff", padding:"15px 34px", fontSize:"15px", fontWeight:"800", minWidth:"260px", boxShadow:"0 12px 24px rgba(5,150,105,0.24)", cursor:"pointer" },
    helper:      { color:"#64748b", fontSize:"13px", margin:0, lineHeight:1.6 },
    serviceGrid: { display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:"10px", marginTop:"10px" },
    serviceCell: { display:"flex", alignItems:"center", gap:"8px", padding:"10px 14px", borderRadius:"12px", border:"1px solid #bbf7d0", background:"#fff" },
    serviceCellLabel:{ fontSize:"14px", fontWeight:"600", color:"#0f172a" },
    errorText:   { color:"#dc2626", fontSize:"14px", fontWeight:"600", marginTop:"12px" },
    groupHeading:{ fontSize:"13px", fontWeight:"800", color:"#059669", textTransform:"uppercase", letterSpacing:"0.04em", marginTop:"16px", marginBottom:"8px" },
    groupHeadingClinical:{ color:"#b45309" },
    yesNoRow:    { display:"flex", gap:"8px" },
    yesNoBtn:    { flex:1, padding:"12px 0", borderRadius:"12px", border:"1.5px solid #d6deeb", background:"#fff", color:"#334155", fontWeight:"700", fontSize:"14px", cursor:"pointer", fontFamily:"inherit" },
    yesNoBtnActive:{ border:"1.5px solid #059669", background:"linear-gradient(135deg,#059669,#10b981)", color:"#fff" },
    validationBox:{ display:"flex", gap:"12px", alignItems:"flex-start", borderRadius:"16px", padding:"16px 18px", marginTop:"16px" },
    validationBoxClinical:{ background:"#fffbeb", border:"1.5px solid #fde68a" },
    validationBoxStandard:{ background:"#ecfdf5", border:"1.5px solid #a7f3d0" },
    validationTitle:{ fontSize:"14.5px", fontWeight:"800", margin:"0 0 4px" },
    validationText:{ fontSize:"13px", color:"#4b5563", margin:0, lineHeight:1.6 },
    validationLink:{ color:"#b45309", fontWeight:"800", textDecoration:"underline" },
    popupOverlay:{ position:"fixed", inset:0, backgroundColor:"rgba(15,23,42,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50 },
    popupCard:   { background:"#fff", borderRadius:"18px", padding:"22px 24px", maxWidth:"360px", width:"90%", textAlign:"center", boxShadow:"0 20px 40px rgba(15,23,42,0.25)" },
    popupTitle:  { margin:"10px 0 6px", fontSize:"1.1rem", fontWeight:"800", color:"#065f46" },
    popupText:   { margin:0, fontSize:"14px", color:"#4b5563" },
    popupButton: { marginTop:"16px", borderRadius:"999px", border:"none", background:"#059669", color:"#fff", fontSize:"14px", fontWeight:"700", padding:"10px 24px", cursor:"pointer" },
  };

  return (
    <>
      {showThankYou && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupCard}>
            <FaCheckCircle color="#059669" size={32}/>
            <h3 style={styles.popupTitle}>Thank you!</h3>
            <p style={styles.popupText}>Your requirement has been submitted. We will get back to you shortly.</p>
            <button type="button" style={styles.popupButton} onClick={() => { setShowThankYou(false); navigate("/"); }}>OK</button>
          </div>
        </div>
      )}

      <div style={styles.page}>
        <div style={styles.wrapper}>

          {/* Hero */}
          <div style={styles.hero}>
            <div style={styles.heroBadge}>🏥 {serviceLabel}</div>
            <h1 style={styles.heroTitle}>Patient Care at Home</h1>
            <p style={styles.heroText}>
              Post your patient care requirement with location details and salary offer — every relevant care service is included.
            </p>
          </div>

          {step === "form" && (
          <form style={styles.formCard} onSubmit={handleNext}>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>Patient Care Requirement Form</h2>
                <p style={styles.formSub}>Fill in personal details, review the included care services, and mention your salary offer.</p>
              </div>

              <div style={styles.body}>
                {/* Your Contact Details */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaUser color="#059669"/> Your Contact Details</h3>
                  <div style={styles.grid}>
                    {[
                      { id:"username",     label:"Your Name",     icon:<FaUser/>,          type:"text",  required:true },
                      { id:"mobilenumber", label:"Mobile Number",  icon:<FaPhoneAlt/>,      type:"tel",   required:true, pattern:"[0-9]{10}", minLength:10, maxLength:10 },
                      { id:"email",        label:"Email",          icon:<FaEnvelope/>,      type:"email", required:true },
                      { id:"address",      label:"Address",        icon:<FaMapMarkerAlt/>,  type:"text",  required:true },
                    ].map((f) => (
                      <div key={f.id} style={styles.field}>
                        <label htmlFor={f.id} style={styles.label}>{f.icon} {f.label}</label>
                        <input id={f.id} style={styles.input} type={f.type} name={f.id}
                          value={formData[f.id]} onChange={handleInputChange}
                          required={f.required} pattern={f.pattern}
                          minLength={f.minLength} maxLength={f.maxLength}
                        />
                      </div>
                    ))}

                    {/* City */}
                    <div style={styles.field}>
                      <label htmlFor="city" style={styles.label}><FaMapMarkerAlt/> City</label>
                      <select id="city" style={styles.select} name="city" value={formData.city} onChange={handleInputChange} required>
                        <option value="">Select City</option>
                        {Object.keys(areaOptions).map((c) => (
                          <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                        ))}
                      </select>
                    </div>

                    {/* Area */}
                    <div style={styles.field}>
                      <label htmlFor="area" style={styles.label}><FaMapMarkerAlt/> Area</label>
                      <select id="area" style={styles.select} name="area" value={formData.area} onChange={handleInputChange} required disabled={!formData.city}>
                        <option value="">Select Area</option>
                        {availableAreas.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </div>

                    {/* Language */}
                    <div style={styles.field}>
                      <label htmlFor="language" style={styles.label}><FaLanguage/> Language</label>
                      <select id="language" style={styles.select} name="language" value={formData.language} onChange={handleInputChange} required>
                        <option value="">Select Language</option>
                        {["Tamil","Telugu","Kannada","Malayalam","Hindi","English"].map((l) => (
                          <option key={l} value={l.toLowerCase()}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* About the Patient */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaHeartbeat color="#059669"/> Patient Details</h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="patientName" style={styles.label}><FaUser/> Patient Name</label>
                      <input id="patientName" style={styles.input} type="text" name="patientName"
                        value={formData.patientName} onChange={handleInputChange} required
                      />
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="patientAge" style={styles.label}><FaUser/> Age</label>
                      <input id="patientAge" style={styles.input} type="number" name="patientAge"
                        min="0" max="120" value={formData.patientAge} onChange={handleInputChange} required
                      />
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="patientGender" style={styles.label}><FaUserTag/> Gender</label>
                      <select id="patientGender" style={styles.select} name="patientGender" value={formData.patientGender} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="relationship" style={styles.label}><FaUserTag/> Relationship to You</label>
                      <select id="relationship" style={styles.select} name="relationship" value={formData.relationship} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        {RELATIONSHIP_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                    <div style={{ ...styles.field, gridColumn: "1 / -1" }}>
                      <label htmlFor="currentCondition" style={styles.label}><FaProcedures/> Current Condition</label>
                      <input id="currentCondition" style={styles.input} type="text" name="currentCondition"
                        placeholder="e.g. Recovering from hip surgery, stroke recovery, diabetic, cardiac condition…"
                        value={formData.currentCondition} onChange={handleInputChange} required
                      />
                    </div>

                    <div style={styles.field}>
                      <span style={styles.label}><FaProcedures/> Post-Surgery?</span>
                      <div style={styles.yesNoRow}>
                        {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                          <button key={lbl} type="button"
                            style={{ ...styles.yesNoBtn, ...(formData.postSurgery === val ? styles.yesNoBtnActive : {}) }}
                            onClick={() => handleYesNoToggle("postSurgery", val)}
                          >{lbl}</button>
                        ))}
                      </div>
                    </div>
                    <div style={styles.field}>
                      <span style={styles.label}><FaProcedures/> Bedridden?</span>
                      <div style={styles.yesNoRow}>
                        {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                          <button key={lbl} type="button"
                            style={{ ...styles.yesNoBtn, ...(formData.bedridden === val ? styles.yesNoBtnActive : {}) }}
                            onClick={() => handleYesNoToggle("bedridden", val)}
                          >{lbl}</button>
                        ))}
                      </div>
                    </div>
                    <div style={styles.field}>
                      <span style={styles.label}><FaProcedures/> Hospital Discharge?</span>
                      <div style={styles.yesNoRow}>
                        {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                          <button key={lbl} type="button"
                            style={{ ...styles.yesNoBtn, ...(formData.hospitalDischarge === val ? styles.yesNoBtnActive : {}) }}
                            onClick={() => handleYesNoToggle("hospitalDischarge", val)}
                          >{lbl}</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: "20px" }}>
                    <label htmlFor="doctorInstructions" style={styles.label}><FaInfoCircle/> Doctor/Family Instructions</label>
                    <textarea id="doctorInstructions" style={styles.textarea} name="doctorInstructions"
                      value={formData.doctorInstructions} onChange={handleInputChange}
                      placeholder="Medication timing, dietary restrictions, doctor's follow-up notes, anything the caretaker should know."
                    />
                  </div>
                </div>

                {/* Care Services Required */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaHandsHelping color="#059669"/> Care Services Included</h3>
                  <p style={styles.helper}>Every service below, across all care types, is included for this booking.</p>

                  {PATIENT_CARE_GROUPS.map((g) => (
                    <div key={g.group}>
                      <div style={{ ...styles.groupHeading, ...(g.clinical ? styles.groupHeadingClinical : {}) }}>
                        {g.group}{g.clinical ? " (Clinical)" : ""}
                      </div>
                      <div style={styles.serviceGrid}>
                        {g.options.map((opt) => (
                          <div key={opt.value} style={styles.serviceCell}>
                            <FaCheckCircle color="#059669" size={14} />
                            <span style={styles.serviceCellLabel}>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Important validation — does the selected requirement need clinical/nursing skill? */}
                  {formData.service_type.length > 0 && (
                    requiresClinicalSkill ? (
                      <div style={{ ...styles.validationBox, ...styles.validationBoxClinical }}>
                        <FaExclamationTriangle color="#b45309" size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                        <div>
                          <p style={{ ...styles.validationTitle, color: "#92400e" }}>Clinical/Nursing Skill Required</p>
                          <p style={styles.validationText}>
                            Some of the services you selected (vital monitoring and/or nursing procedures) need a
                            trained nurse, not a general patient attendant. We'll match this requirement with a
                            qualified caretaker from our{" "}
                            <Link to="/service/caretaker-service/medical-care-representative" style={styles.validationLink}>
                              Medical Representative / Medical Care Representative
                            </Link>{" "}
                            team.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div style={{ ...styles.validationBox, ...styles.validationBoxStandard }}>
                        <FaShieldAlt color="#059669" size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                        <div>
                          <p style={{ ...styles.validationTitle, color: "#065f46" }}>Standard Patient Care</p>
                          <p style={styles.validationText}>
                            Your selected services can be handled by a general patient caretaker — no clinical
                            certification required.
                          </p>
                        </div>
                      </div>
                    )
                  )}

                  {submitError && <p style={styles.errorText}>{submitError}</p>}
                </div>

                {/* Timing & Caretaker Preference */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaClock color="#059669"/> Timing & Caretaker Preference</h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="gender" style={styles.label}><FaUserTag/> Preferred Caretaker Gender</label>
                      <select id="gender" style={styles.select} name="gender" value={formData.gender} onChange={handleInputChange} required>
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="any">Any</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="urgencyStatus" style={styles.label}><FaInfoCircle/> Urgency</label>
                      <select id="urgencyStatus" style={styles.select} name="urgencyStatus" value={formData.urgencyStatus} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="within_days">Within Days</option>
                        <option value="within_months">Within Months</option>
                        <option value="just_enquiry">Just Enquiry</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="duration" style={styles.label}><FaClock/> Duration</label>
                      <input id="duration" style={styles.input} type="text" name="duration"
                        placeholder="In months" value={formData.duration} onChange={handleInputChange} required
                      />
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="shiftDuration" style={styles.label}><FaClock/> Hours</label>
                      <select id="shiftDuration" style={styles.select} name="shiftDuration" value={formData.shiftDuration} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="2hrs">2 Hours</option>
                        <option value="4hrs">4 Hours</option>
                        <option value="6hrs">6 Hours</option>
                        <option value="12hrs">12 Hours</option>
                        <option value="live_24hrs">Live-in (24 Hours)</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="shiftTime" style={styles.label}><FaClock/> Shift</label>
                      <select id="shiftTime" style={styles.select} name="shiftTime" value={formData.shiftTime} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                        <option value="evening">Evening</option>
                        <option value="full_day">Full Day</option>
                      </select>
                    </div>
                    <div style={{ ...styles.field, position: "relative" }}>
                      <label htmlFor="experienceLevel" style={styles.label}>
                        <FaUserTag/> Caretaker Experience
                        <button type="button" style={styles.infoBtn} onClick={() => setShowExperienceInfo((v) => !v)} aria-label="What do these experience levels mean?">
                          <FaInfoCircle/>
                        </button>
                      </label>
                      <select id="experienceLevel" style={styles.select} name="experienceLevel" value={formData.experienceLevel} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="expert">Expert</option>
                        <option value="normal">Normal</option>
                        <option value="assistance">Assistance</option>
                      </select>
                      {showExperienceInfo && (
                        <div style={styles.infoPopover}>
                          <p style={{ margin: "0 0 8px", fontSize: "13px", lineHeight: 1.6, color: "#374151" }}><strong>Expert:</strong> Highly experienced, trained professional for complex or specialised care needs.</p>
                          <p style={{ margin: "0 0 8px", fontSize: "13px", lineHeight: 1.6, color: "#374151" }}><strong>Normal:</strong> Standard experienced caretaker for everyday, routine care.</p>
                          <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.6, color: "#374151" }}><strong>Assistance:</strong> Helper-level support for basic day-to-day tasks.</p>
                        </div>
                      )}
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="salaryOffered" style={styles.label}><FaMoneyBillWave/> Salary Offered (₹)</label>
                      <input id="salaryOffered" style={styles.input} type="number" name="salaryOffered"
                        value={formData.salaryOffered} onChange={handleInputChange} placeholder="e.g. 15000" required
                      />
                    </div>
                  </div>
                </div>

                <div style={styles.submitWrap}>
                  <button type="submit" style={styles.submitBtn} disabled={isSubmitting}>
                    Next
                  </button>
                </div>
              </div>
            </form>
          )}

          {step === "terms" && (
            <div style={styles.formCard}>
              <TermsAcceptStep
                onBack={() => setStep("form")}
                onSubmit={submitRequirement}
                isSubmitting={isSubmitting}
                submitError={submitError}
                serviceLabel={serviceLabel}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PatientCare;
